import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { FAQLayoutId } from '@/constants/layoutTypes';

interface FAQSectionProps {
    faqs: Array<{ question: string; answer: string }>;
    title?: string;
    primaryColor: string;
    isEditing: boolean;
    onEdit: (section: string, field: string, value: string, index?: number) => void;
    sizeClasses: { heading: string; subheading: string; body: string; small: string };
    layout?: FAQLayoutId;
    onArrayAction?: (action: 'add' | 'delete', section: 'features' | 'faq', index?: number) => void;
}

// ── Shared editor controls ──────────────────────────────────────────────────

function DeleteFAQBtn({ onClick }: { onClick: () => void }) {
    return (
        <button
            onClick={(e) => { e.stopPropagation(); onClick(); }}
            className="shrink-0 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md opacity-0 group-hover/item:opacity-100 transition-opacity ml-2"
            title="Remove question"
        >
            <Icon name="XMarkIcon" className="w-3.5 h-3.5" />
        </button>
    );
}

function AddQuestionBtn({ onClick }: { onClick: () => void }) {
    return (
        <div className="flex justify-center mt-8">
            <button
                onClick={onClick}
                className="flex items-center gap-2 px-5 py-2.5 border-2 border-dashed border-blue-400 text-blue-600 rounded-xl hover:bg-blue-50 transition-colors text-sm font-medium"
            >
                <Icon name="PlusIcon" className="w-4 h-4" />
                Add Question
            </button>
        </div>
    );
}

// ── Shared Editable Components ─────────────────────────────────────────────

function SectionTitle({
    title, sizeClasses, editClass, isEditing, onEdit
}: {
    title: string; sizeClasses: FAQSectionProps['sizeClasses'];
    editClass: string; isEditing: boolean;
    onEdit: FAQSectionProps['onEdit'];
}) {
    return (
        <h2
            className={`${sizeClasses.heading} font-bold text-center text-gray-900 mb-4 ${editClass}`}
            contentEditable={isEditing}
            suppressContentEditableWarning
            onBlur={(e) => onEdit('faq', 'title', e.currentTarget.textContent || '')}
        >
            {title || 'Frequently Asked Questions'}
        </h2>
    );
}

// ── Layout 1: Accordion (Enhanced Original) ────────────────────────────────

function FAQAccordion({ faqs, title, primaryColor, isEditing, onEdit, sizeClasses, onArrayAction }: FAQSectionProps) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-2' : '';
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    const toggleFAQ = (idx: number) => {
        if (!isEditing) setOpenIndex(openIndex === idx ? null : idx);
    };

    return (
        <section id="faq" className="py-20 px-6 bg-white @container">
            <div className="max-w-3xl mx-auto">
                <SectionTitle title={title || ''} sizeClasses={sizeClasses} editClass={editClass} isEditing={isEditing} onEdit={onEdit} />
                <p className="text-center text-gray-500 mb-12 text-sm">Everything you need to know</p>

                <div className="space-y-3">
                    {faqs.map((item, idx) => {
                        const isOpen = openIndex === idx;
                        return (
                            <div
                                key={idx}
                                className={`group/item rounded-xl border transition-all duration-200 ${
                                    isOpen
                                        ? 'border-gray-300 bg-white shadow-sm'
                                        : 'border-gray-200 bg-gray-50/50 hover:border-gray-300'
                                }`}
                            >
                                <div className="flex items-center">
                                    <button
                                        onClick={() => toggleFAQ(idx)}
                                        className={`flex-1 flex items-center justify-between p-5 text-left focus:outline-none ${isEditing ? 'cursor-text' : 'cursor-pointer'}`}
                                    >
                                        <h3
                                            className={`${sizeClasses.subheading} font-semibold text-gray-900 pr-8 ${editClass}`}
                                            contentEditable={isEditing}
                                            suppressContentEditableWarning
                                            onBlur={(e) => onEdit('faq', 'question', e.currentTarget.textContent || '', idx)}
                                        >
                                            {item.question}
                                        </h3>
                                        <div
                                            className={`shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                                                isOpen ? 'rotate-180' : ''
                                            }`}
                                            style={{ backgroundColor: isOpen ? `${primaryColor}15` : 'transparent', color: primaryColor }}
                                        >
                                            <Icon name="ChevronDownIcon" className="w-5 h-5" />
                                        </div>
                                    </button>
                                    {isEditing && onArrayAction && (
                                        <div className="pr-4">
                                            <DeleteFAQBtn onClick={() => onArrayAction('delete', 'faq', idx)} />
                                        </div>
                                    )}
                                </div>

                                <div
                                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                                >
                                    <div className="overflow-hidden">
                                        <p
                                            className={`${sizeClasses.body} text-gray-600 leading-relaxed px-5 pb-5 ${editClass}`}
                                            contentEditable={isEditing}
                                            suppressContentEditableWarning
                                            onBlur={(e) => onEdit('faq', 'answer', e.currentTarget.textContent || '', idx)}
                                        >
                                            {item.answer}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {isEditing && onArrayAction && (
                    <AddQuestionBtn onClick={() => onArrayAction('add', 'faq')} />
                )}
            </div>
        </section>
    );
}

// ── Layout 2: Two Column ───────────────────────────────────────────────────

function FAQTwoColumn({ faqs, title, primaryColor, isEditing, onEdit, sizeClasses, onArrayAction }: FAQSectionProps) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-2' : '';
    const [openIndex, setOpenIndex] = useState<number | null>(0);

    const toggleFAQ = (idx: number) => {
        if (!isEditing) setOpenIndex(openIndex === idx ? null : idx);
    };

    const midpoint = Math.ceil(faqs.length / 2);
    const leftColumn = faqs.slice(0, midpoint);
    const rightColumn = faqs.slice(midpoint);

    const renderFAQItem = (item: { question: string; answer: string }, idx: number) => {
        const isOpen = openIndex === idx;
        return (
            <div key={idx} className="group/item border-b border-gray-100 last:border-0">
                <div className={`w-full flex items-start justify-between py-4 gap-3 ${isEditing ? 'cursor-text' : 'cursor-pointer'}`}
                    onClick={() => toggleFAQ(idx)}
                >
                    <h3
                        className={`${sizeClasses.body} font-semibold text-gray-900 flex-1 ${editClass}`}
                        contentEditable={isEditing}
                        suppressContentEditableWarning
                        onBlur={(e) => onEdit('faq', 'question', e.currentTarget.textContent || '', idx)}
                    >
                        {item.question}
                    </h3>
                    <div className="flex items-center gap-1 shrink-0">
                        <div
                            className={`mt-0.5 transition-transform duration-300 ${isOpen ? 'rotate-45' : ''}`}
                            style={{ color: primaryColor }}
                        >
                            <Icon name="PlusIcon" className="w-5 h-5" />
                        </div>
                        {isEditing && onArrayAction && (
                            <DeleteFAQBtn onClick={() => onArrayAction('delete', 'faq', idx)} />
                        )}
                    </div>
                </div>
                <div className={`grid transition-[grid-template-rows] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}>
                    <div className="overflow-hidden">
                        <p
                            className={`${sizeClasses.body} text-gray-500 leading-relaxed pb-4 ${editClass}`}
                            contentEditable={isEditing}
                            suppressContentEditableWarning
                            onBlur={(e) => onEdit('faq', 'answer', e.currentTarget.textContent || '', idx)}
                        >
                            {item.answer}
                        </p>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <section id="faq" className="py-20 px-6 bg-white @container">
            <div className="max-w-6xl mx-auto">
                <SectionTitle title={title || ''} sizeClasses={sizeClasses} editClass={editClass} isEditing={isEditing} onEdit={onEdit} />
                <p className="text-center text-gray-500 mb-12 text-sm">Find answers to the most common questions</p>

                <div className="grid grid-cols-1 @3xl:grid-cols-2 gap-x-16 gap-y-0">
                    <div>
                        {leftColumn.map((item, idx) => renderFAQItem(item, idx))}
                    </div>
                    <div>
                        {rightColumn.map((item, idx) => renderFAQItem(item, idx + midpoint))}
                    </div>
                </div>

                {isEditing && onArrayAction && (
                    <AddQuestionBtn onClick={() => onArrayAction('add', 'faq')} />
                )}
            </div>
        </section>
    );
}

// ── Layout 3: Side by Side ─────────────────────────────────────────────────

function FAQSideBySide({ faqs, title, primaryColor, isEditing, onEdit, sizeClasses, onArrayAction }: FAQSectionProps) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-2' : '';

    return (
        <section id="faq" className="py-20 px-6 bg-gray-50 @container">
            <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 @3xl:grid-cols-[280px_1fr] gap-12">
                    {/* Left: sticky title */}
                    <div className="@3xl:sticky @3xl:top-24 @3xl:self-start">
                        <h2
                            className={`${sizeClasses.heading} font-bold text-gray-900 mb-3 ${editClass}`}
                            contentEditable={isEditing}
                            suppressContentEditableWarning
                            onBlur={(e) => onEdit('faq', 'title', e.currentTarget.textContent || '')}
                        >
                            {title || 'Frequently Asked Questions'}
                        </h2>
                        <p className="text-gray-500 text-sm leading-relaxed">
                            Can&apos;t find what you&apos;re looking for? Reach out to our support team.
                        </p>
                        <div className="mt-4 w-12 h-1 rounded-full" style={{ backgroundColor: primaryColor }} />
                    </div>

                    {/* Right: Q&A list */}
                    <div className="space-y-0">
                        {faqs.map((item, idx) => (
                            <div key={idx} className="group/item py-6 border-b border-gray-200 first:pt-0 last:border-0">
                                <div className="flex gap-4">
                                    <div
                                        className="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-sm font-bold text-white mt-0.5"
                                        style={{ backgroundColor: primaryColor }}
                                    >
                                        {idx + 1}
                                    </div>
                                    <div className="flex-1">
                                        <div className="flex items-start gap-2">
                                            <h3
                                                className={`${sizeClasses.subheading} font-semibold text-gray-900 mb-2 flex-1 ${editClass}`}
                                                contentEditable={isEditing}
                                                suppressContentEditableWarning
                                                onBlur={(e) => onEdit('faq', 'question', e.currentTarget.textContent || '', idx)}
                                            >
                                                {item.question}
                                            </h3>
                                            {isEditing && onArrayAction && (
                                                <DeleteFAQBtn onClick={() => onArrayAction('delete', 'faq', idx)} />
                                            )}
                                        </div>
                                        <p
                                            className={`${sizeClasses.body} text-gray-500 leading-relaxed ${editClass}`}
                                            contentEditable={isEditing}
                                            suppressContentEditableWarning
                                            onBlur={(e) => onEdit('faq', 'answer', e.currentTarget.textContent || '', idx)}
                                        >
                                            {item.answer}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {isEditing && onArrayAction && (
                            <AddQuestionBtn onClick={() => onArrayAction('add', 'faq')} />
                        )}
                    </div>
                </div>
            </div>
        </section>
    );
}

// ── Layout 4: Cards Grid ───────────────────────────────────────────────────

function FAQCardsGrid({ faqs, title, primaryColor, isEditing, onEdit, sizeClasses, onArrayAction }: FAQSectionProps) {
    const editClass = isEditing ? 'outline-dashed outline-2 outline-blue-400 outline-offset-2' : '';

    return (
        <section id="faq" className="py-20 px-6 bg-white @container">
            <div className="max-w-6xl mx-auto">
                <SectionTitle title={title || ''} sizeClasses={sizeClasses} editClass={editClass} isEditing={isEditing} onEdit={onEdit} />
                <p className="text-center text-gray-500 mb-12 text-sm">Quick answers at a glance</p>

                <div className={`grid grid-cols-1 gap-4 ${
                    faqs.length === 1 ? 'max-w-lg mx-auto' :
                    faqs.length === 2 ? '@3xl:grid-cols-2 max-w-4xl mx-auto' :
                    '@2xl:grid-cols-2 @4xl:grid-cols-3'
                }`}>
                    {faqs.map((item, idx) => (
                        <div
                            key={idx}
                            className="relative group/item p-6 rounded-2xl border border-gray-200 bg-white hover:border-gray-300 hover:shadow-md transition-all duration-200"
                        >
                            {isEditing && onArrayAction && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); onArrayAction('delete', 'faq', idx); }}
                                    className="absolute -top-2 -right-2 z-20 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center shadow-md opacity-0 group-hover/item:opacity-100 transition-opacity"
                                    title="Remove question"
                                >
                                    <Icon name="XMarkIcon" className="w-3.5 h-3.5" />
                                </button>
                            )}

                            {/* Icon / Number header */}
                            <div className="flex items-center gap-3 mb-4">
                                <div
                                    className="w-9 h-9 rounded-xl flex items-center justify-center"
                                    style={{ backgroundColor: `${primaryColor}12` }}
                                >
                                    <Icon name="QuestionMarkCircleIcon" className="w-5 h-5" style={{ color: primaryColor }} />
                                </div>
                            </div>

                            <h3
                                className={`${sizeClasses.subheading} font-semibold text-gray-900 mb-3 leading-snug ${editClass}`}
                                contentEditable={isEditing}
                                suppressContentEditableWarning
                                onBlur={(e) => onEdit('faq', 'question', e.currentTarget.textContent || '', idx)}
                            >
                                {item.question}
                            </h3>
                            <p
                                className={`${sizeClasses.body} text-gray-500 leading-relaxed ${editClass}`}
                                contentEditable={isEditing}
                                suppressContentEditableWarning
                                onBlur={(e) => onEdit('faq', 'answer', e.currentTarget.textContent || '', idx)}
                            >
                                {item.answer}
                            </p>
                        </div>
                    ))}
                </div>

                {isEditing && onArrayAction && (
                    <AddQuestionBtn onClick={() => onArrayAction('add', 'faq')} />
                )}
            </div>
        </section>
    );
}

// ── Main Export ─────────────────────────────────────────────────────────────

export function FAQSection(props: FAQSectionProps) {
    const { layout = 'accordion' } = props;

    switch (layout) {
        case 'two-column':
            return <FAQTwoColumn {...props} />;
        case 'side-by-side':
            return <FAQSideBySide {...props} />;
        case 'cards-grid':
            return <FAQCardsGrid {...props} />;
        case 'accordion':
        default:
            return <FAQAccordion {...props} />;
    }
}
